import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { AlertCircle, CheckCircle2, RefreshCw, Trash2, Mail, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const fmtAmount = (cents) => {
  if (typeof cents !== 'number') return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(cents);
};

export default function PaymentErrorsTab() {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState({ total: 0, unresolved: 0 });
  const [onlyUnresolved, setOnlyUnresolved] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payment-errors`, { params: { only_unresolved: onlyUnresolved, limit: 100 } });
      setErrors(res.data.errors || []);
      setStats({ total: res.data.total || 0, unresolved: res.data.unresolved || 0 });
    } catch (e) {
      toast.error('Erreur lors du chargement des logs');
    }
    setLoading(false);
  };

  useEffect(() => { fetchErrors(); /* eslint-disable-next-line */ }, [onlyUnresolved]);

  const markResolved = async (id) => {
    try {
      await axios.post(`${API}/admin/payment-errors/${id}/resolve`);
      toast.success('Erreur marquée comme résolue');
      fetchErrors();
    } catch { toast.error('Échec de la mise à jour'); }
  };

  const deleteError = async (id) => {
    if (!window.confirm('Supprimer définitivement cette entrée ?')) return;
    try {
      await axios.delete(`${API}/admin/payment-errors/${id}`);
      toast.success('Entrée supprimée');
      fetchErrors();
    } catch { toast.error('Échec de la suppression'); }
  };

  const copyEmail = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    toast.success('Email copié');
  };

  return (
    <div className="space-y-6" data-testid="payment-errors-tab">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-navy flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-rose-500" />
            Logs Stripe — Erreurs paiement
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Suivez les clients qui n'ont pas pu finaliser leur paiement et rappelez-les avant qu'ils ne partent ailleurs.
          </p>
        </div>
        <Button variant="outline" onClick={fetchErrors} disabled={loading} data-testid="refresh-payment-errors">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-rose-500">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Erreurs non résolues</p>
            <p className="text-3xl font-bold text-rose-600" data-testid="unresolved-count">{stats.unresolved}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-400">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total historique</p>
            <p className="text-3xl font-bold text-navy">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Filtre</p>
              <p className="text-sm font-semibold text-navy">{onlyUnresolved ? 'Non résolues uniquement' : 'Toutes les erreurs'}</p>
            </div>
            <Switch checked={onlyUnresolved} onCheckedChange={setOnlyUnresolved} data-testid="filter-unresolved" />
          </CardContent>
        </Card>
      </div>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Liste des erreurs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Chargement…</p>
          ) : errors.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
              <p className="font-semibold text-navy">Aucune erreur paiement {onlyUnresolved ? 'non résolue' : ''} pour le moment.</p>
              <p className="text-sm mt-1">Excellent ! Tous les paiements passent bien.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {errors.map((err) => (
                <div
                  key={err.id}
                  className={`p-4 rounded-lg border-2 ${err.resolved ? 'border-slate-200 bg-slate-50/50 opacity-70' : 'border-rose-200 bg-rose-50/30'}`}
                  data-testid={`payment-error-${err.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-navy text-sm">{err.customer_email || '(pas d\'email)'}</span>
                        {err.customer_email && (
                          <button onClick={() => copyEmail(err.customer_email)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                            <Copy className="w-3 h-3" /> copier
                          </button>
                        )}
                        <Badge variant="outline" className="text-xs">{err.package_id || 'pkg ?'}</Badge>
                        <span className="text-xs text-muted-foreground">{fmtAmount(err.amount)}</span>
                        {err.partner_subdomain && <Badge className="text-xs bg-purple-100 text-purple-700 border-0">@{err.partner_subdomain}</Badge>}
                        {err.resolved && <Badge className="text-xs bg-emerald-100 text-emerald-700 border-0">Résolue</Badge>}
                      </div>
                      <p className="text-xs text-rose-700 font-mono break-words mb-1">{err.error_message}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {fmtDate(err.created_at)} · payload {err.project_data_size || 0} caractères
                        {err.resolved && err.resolved_by && ` · résolue par ${err.resolved_by}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {err.customer_email && !err.resolved && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`mailto:${err.customer_email}?subject=Votre%20paiement%20sur%20notre%20plateforme&body=Bonjour%2C%0A%0ANous%20avons%20remarqu%C3%A9%20que%20votre%20paiement%20n'a%20pas%20pu%20aboutir.%20Comment%20puis-je%20vous%20aider%20%3F%0A%0ABien%20cordialement%2C`)}
                          data-testid={`mail-${err.id}`}
                        >
                          <Mail className="w-4 h-4 mr-1" /> Email
                        </Button>
                      )}
                      {!err.resolved && (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => markResolved(err.id)}
                          data-testid={`resolve-${err.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Résolu
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        onClick={() => deleteError(err.id)}
                        data-testid={`delete-${err.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
